import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription, take, zip } from 'rxjs';
import { DfMeDropdownComponent } from '../../../shared/components/df-me-dropdown/df-me-dropdown.component';
import { JSONExportHeader, JSONExportService } from '../../../shared/_services/JSONExport.service';
import { DiscModalViewerComponent } from '../../shared/components/disc-modal-viewer/disc-modal-viewer.component';
import { StmtIdBtnService } from '../../shared/components/stmt-id-btn/stmt-id-btn.service';
import { DSTSharedService } from '../../shared/dst-shared.service';
import { DSTChildFormState } from '../../shared/models/dst-child-form-state.model';
import { DSTStatement } from '../../shared/models/dst-statement.model';
import { CreateDisclosureService, DSTTaxYear } from '../../statement-tracker/create-disclosure/create-new/create-disclosure.service';
import { DSTDynamicComponentResolverService } from '../../statement-tracker/create-disclosure/shared/dst-dynamic-component-resolver.service';
import { TrackerTabsService } from '../../statement-tracker/tracker-tabs.service';
import { ReportsService } from '../reports.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { generalConfigFromGlobal, hydratedGlobalParams } from '../../../shared/workspace-element-context';


import {
  buildDstTaxYearItems,
  resolveDstTaxYearFromEvent,
  setDstTaxYearAriaLabel,
  syncDstTaxYearItem
} from '../../shared/utils/dst-tax-year-appkit.support';
import { getAppkitDropdownSelection } from '../../../shared/utils/appkit-dropdown.util';

@Component({
    selector: 'gtw-sec332-report',
    templateUrl: './sec-332-report.component.html',
    styleUrls: ['../../shared/styles/dst-shared.scss', '../../shared/mat-table/dst-mat-table.scss', './sec-332-report.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class Sec332ReportComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);


  @ViewChild(DiscModalViewerComponent) modalViewerComponent!: DiscModalViewerComponent;
  @ViewChild(DfMeDropdownComponent) dfMeDropdownComponent!: DfMeDropdownComponent;
  @ViewChild(MatSort) matSort!: MatSort;

  data: any[] = [];
  dataSource = new MatTableDataSource<any>([]);
  displayedColumns: string[] = [];
  cols: any[] = [
    {
      field: 'STATEMENT_ID',
      header: 'Statement Id',
      hidden: false,
      width: '145px'
    },
    {
      field: 'PROJECT_NAME',
      header: 'Project',
      hidden: false,
      width: '105px'
    },
    {
      field: 'PRIMARY_LEID',
      header: 'Transferor (Primary LEID)',
      hidden: false,
      width: '155px'
    },
    {
      field: 'PRIMARY_ENTITY',
      header: 'Transferor Name',
      hidden: false,
      width: '260px'
    },
    {
      field: 'PRIMARY_BUSINESS',
      header: 'Transferee Business/ME',
      hidden: false,
      width: '260px'
    },
    {
      field: 'FILLING_GROUP',
      header: 'Filling group',
      hidden: false,
      width: '145px'
    },
    {
      field: 'SECONDARY_LEID',
      header: 'Transferee (Secondary LEID)',
      hidden: false,
      width: '175px'
    },
    {
      field: 'SECONDARY_ENTITY',
      header: 'Transferee Name',
      hidden: false,
      width: '260px'
    },
    {
      field: 'LIQUIDATIONDATE_VALUE',
      header: 'Date of Distribution',
      hidden: false,
      width: '195px'
    },
    {
      field: 'IMPORTATIONPROPERTYDESC',
      header: 'Importation property distributed in a loss importation transaction',
      hidden: false,
      width: '275px'
    },
    {
      field: 'GAINLOSSPROPERTYDESC',
      header: 'Property with respect to which any gain or loss was recognized on the distribution',
      hidden: false,
      width: '335px'
    },
    {
      field: 'DESCRIPTION',
      header: 'Property Distributed',
      hidden: false,
      width: '375px'
    },
    {
      field: 'BASISAMT_VALUE',
      header: 'Basis Amount',
      hidden: false,
      width: '155px'
    },
    {
      field: 'BASIS_CURRENCY',
      header: 'Basis Currency',
      hidden: false,
      width: '155px'
    },
    {
      field: 'BASIS_NAME',
      header: 'Basis Type',
      hidden: false,
      width: '125px'
    },
    {
      field: 'FMVAMT_VALUE',
      header: 'FMV Amount',
      hidden: false,
      width: '150px'
    },
    {
      field: 'FMV_CURRENCY',
      header: 'FMV Currency',
      hidden: false,
      width: '155px'
    },
    {
      field: 'PLR_VALUE',
      header: 'PLR Control Number',
      hidden: false,
      width: '200px'
    },
    {
      field: 'ADOPTEDDATE_VALUE',
      header: 'PLR Date',
      hidden: false,
      width: '185px'
    },
    {
      field: 'ACQUIREDDATE_VALUE',
      header: 'Date Liquidation Plan Adopted',
      hidden: false,
      width: '235px'
    },
    {
      field: 'EFFECTIVEDATE_VALUE',
      header: 'Date Liquidation Completed',
      hidden: false,
      width: '180px'
    },
    {
      field: 'LIQUIDATIONSTATUS',
      header: 'Liquidation Status',
      hidden: false,
      width: '220px'
    },
    {
      field: 'ADDITIONALTEXT',
      header: 'Additional Information',
      hidden: false,
      width: '220px'
    },
  ];
  loading: boolean = true;
  taxyearLoaded: boolean = false;
  excelExportFileName: string;

  taxYears: DSTTaxYear[] = [];
  taxYearItems: { value: number; label: string }[] = [];
  selectedTaxYearItem: any = {};
  taxYear: DSTTaxYear | undefined;
  selectedMes: string = '';
  activeRow!: DSTStatement;
  showModalViewer: boolean = false;
  private onDisCancelSubs!: Subscription;
  private contextApplied = false;

  constructor(private cDSerivce: CreateDisclosureService,
    private dstSharedService: DSTSharedService,
    private stmtIdBtnService: StmtIdBtnService,
    private trackerTabsService: TrackerTabsService,
    private dCRService: DSTDynamicComponentResolverService,
    private jsonExportService: JSONExportService,
    private reportsService: ReportsService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService) {

    this.excelExportFileName = 'Reports-332_Report_' + this.dstSharedService.getExcelExportDateString();
    this.displayedColumns = this.cols.filter((c) => !c.hidden).map((c) => c.field);
  }

  ngOnInit(): void {
    this.onDisCancelSubs = this.dCRService.onDisclosureCancel.subscribe(() => {
      this.trackerTabsService.destroyModalTab();
      this.showModalViewer = false;
    });

    this.applyWorkspaceContext();
    this.onDisCancelSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  ngAfterViewInit(): void {
    this.connectMatTableSort();
  }

  ngOnDestroy(): void {
    this.onDisCancelSubs?.unsubscribe();
  }

  getColStyle(col: { width?: string }): Record<string, string> {
    const width = col.width || '100px';
    return { width, minWidth: width, maxWidth: width };
  }

  onMatSortChange(): void {
    this.connectMatTableSort();
  }

  private connectMatTableSort(): void {
    if (this.matSort) {
      this.dataSource.sort = this.matSort;
    }
  }

  private applyWorkspaceContext(): void {
    if (this.contextApplied) return;
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    const gp = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    const user = this.gtwApp.USER_SETTINGS?.user || {};
    const clientKey = String(user.client?.client_key || user.client_key || '');
    const ssoId = String(user.sso_id || '');
    const roleLevel = Number(this.gtwWorkspace.workspaceFactory?.activeScreen?.parent?.access_level || 0);
    const taxYear = String(gp.tax_year || '');
    const scenario = String(gp.scenario || '');
    if (!clientKey && !ssoId) return;
    this.contextApplied = true;
    this.dstSharedService.setClientKey(clientKey);
    this.dstSharedService.setDSTUser(ssoId, roleLevel);
    this.dstSharedService.setSystemLockUnlockStatus(false, roleLevel);
    this.dstSharedService.setTaxYearScenario(taxYear, scenario);
    this.dstSharedService.setBaseURLs(gc);
    const getTaxYears$ = this.cDSerivce.getTaxYears();
    zip(getTaxYears$).pipe(take(1)).subscribe((data) => {
      this.taxYears = data[0];
      this.setTaxYear(taxYear);
      this.dfMeDropdownComponent.loadMEsCustom(this.taxYear?.text.toString()!);
      this.loadReport();
    });
  }

  onTaxYearChange(): void {
    this.taxyearLoaded = true;
    this.dfMeDropdownComponent.loadMEsCustom(this.taxYear?.text.toString()!);
  }

  setTaxYear(taxYear: string): void {
    this.taxYear = this.taxYears.find((item: any) => item.text.toString() === taxYear);
    this.taxYearItems = buildDstTaxYearItems(this.taxYears);
    this.selectedTaxYearItem = syncDstTaxYearItem(this.taxYearItems, this.taxYear, taxYear);
    this.taxyearLoaded = true;
  }

  onTaxYearChanged(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedTaxYearItem = selected || this.selectedTaxYearItem;
    const matched = resolveDstTaxYearFromEvent(event, this.taxYears);
    if (matched) {
      this.taxYear = matched;
      this.onTaxYearChange();
    }
  }

  onTaxYearSelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      this.selectedTaxYearItem = selected;
    }
  }

  setTaxYearAriaLabel(item: any): string {
    return setDstTaxYearAriaLabel(item);
  }

  selectedMeChanged(selected: string): void {
    this.selectedMes = selected;
  }

  loadReport(): void {
    this.data = [];
    this.dataSource.data = [];
    this.loading = true;
    this.reportsService.loadSection332Report(this.taxYear?.text.toString()!, this.selectedMes || '').pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      this.data = data;
      this.data?.forEach((element) => {
        element.statementId = element.STATEMENT_ID;
        element.taxYearId = element.TAXYEARID;
        element.formId = element.FORMID;
        element.formName = element.FORMNAME;
      });
      this.dataSource.data = this.data;
      this.connectMatTableSort();
    }, (error: HttpErrorResponse) => {
      console.error(error);
      this.data = [];
      this.dataSource.data = [];
      this.dstSharedService.showAlert('error', 'Something went wrong while loading the N/A Report grid data, please try again!');
    }).add(() => {
      this.loading = false;
    });
  }

  showViewUpdateStatement(rowData: any): void {
    this.activeRow = rowData;
    this.dstSharedService.setTableActionStatus(true);
    this.dstSharedService.setIsFiled(rowData.IS_FILED!);
    this.stmtIdBtnService.getChildStatements(rowData.STATEMENT_ID).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((childFormStates: DSTChildFormState[]) => {
      this.showModalViewer = true;
      setTimeout(() => {
        this.modalViewerComponent.showMore('dst-disclosure-viewer', childFormStates);
      });
    }, (eMsg: string) => {
      console.log(eMsg);
      this.dstSharedService.showAlert('error', eMsg);
    }).add(() => {
      this.dstSharedService.setTableActionStatus(false);
    });
  }

  afterModalClosed(): void {
    this.trackerTabsService.destroyModalTab();
    this.showModalViewer = false;
  }

  exportToExcel(): void {
    const headersMap: JSONExportHeader[] = [
      {
        field: 'STATEMENT_ID',
        header: 'Statement Id',
      },
      {
        field: 'PROJECT_NAME',
        header: 'Project',
      },
      {
        field: 'PRIMARY_LEID',
        header: 'Transferor (Primary LEID)',
      },
      {
        field: 'PRIMARY_ENTITY',
        header: 'Transferor Name',
      },
      {
        field: 'PRIMARY_BUSINESS',
        header: 'Transferee Business/ME',
      },
      {
        field: 'FILLING_GROUP',
        header: 'Filling group',
      },
      {
        field: 'SECONDARY_LEID',
        header: 'Transferee (Secondary LEID)',
      },
      {
        field: 'SECONDARY_ENTITY',
        header: 'Transferee Name',
      },
      {
        field: 'LIQUIDATIONDATE_VALUE',
        header: 'Date of Distribution',
      },
      {
        field: 'IMPORTATIONPROPERTYDESC',
        header: 'Importation property distributed in a loss importation transaction',
      },
      {
        field: 'GAINLOSSPROPERTYDESC',
        header: 'Property with respect to which any gain or loss was recognized on the distribution',
      },
      {
        field: 'DESCRIPTION',
        header: 'Property Distributed',
      },
      {
        field: 'BASISAMT_VALUE',
        header: 'Basis Amount',
        format: 'number',
      },
      {
        field: 'BASIS_CURRENCY',
        header: 'Basis Currency',
      },
      {
        field: 'BASIS_NAME',
        header: 'Basis Type',
      },
      {
        field: 'FMVAMT_VALUE',
        header: 'FMV Amount',
        format: 'number',
      },
      {
        field: 'FMV_CURRENCY',
        header: 'FMV Currency',
      },
      {
        field: 'PLR_VALUE',
        header: 'PLR Control Number',
      },
      {
        field: 'ADOPTEDDATE_VALUE',
        header: 'PLR Date',
      },
      {
        field: 'ACQUIREDDATE_VALUE',
        header: 'Date Liquidation Plan Adopted',
      },
      {
        field: 'EFFECTIVEDATE_VALUE',
        header: 'Date Liquidation Completed',
      },
      {
        field: 'LIQUIDATIONSTATUS',
        header: 'Liquidation Status',
      },
      {
        field: 'ADDITIONALTEXT',
        header: 'Additional Information',
      }
    ];

    this.jsonExportService.exportToExcel(this.excelExportFileName, this.data, headersMap);
  }

}
